import { useMemo, useState } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useFeatureFlag } from "../../../../common/infrastructure/hooks/useFeatureFlag";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { FeatureFlagKey } from "../../../../common/enums";
import { emailTypeOptions } from "../emailLogs/models/emailLogModel";
import { IPushLogUser } from "../pushLogs/models/pushLogModel";

// Tab order on the page: Имейли, SMS, then Известия (only while app notifications are on).
const EMAIL_TAB_INDEX = 0;
const SMS_TAB_INDEX = 1;
const PUSH_TAB_INDEX = 2;

interface IEmailLogUser {
  id: number;
  name: string;
  email: string;
}

interface ISmsLogUser {
  id: number;
  name: string;
  phone: string;
}

export const useCommunicationLogsFilters = () => {
  const canSeePushLogs = useFeatureFlag(FeatureFlagKey.EnableAppNotifications, false);

  const [activeIndex, setActiveIndex] = useState(EMAIL_TAB_INDEX);
  const [isEmailSidebarOpen, setIsEmailSidebarOpen] = useState(false);
  const [isSmsSidebarOpen, setIsSmsSidebarOpen] = useState(false);
  const [isPushSidebarOpen, setIsPushSidebarOpen] = useState(false);

  const [emailFilterUserIds, setEmailFilterUserIds] = useState<number[] | null>(null);
  const [emailFilterEmailTypes, setEmailFilterEmailTypes] = useState<number[] | null>(null);
  const [smsFilterUserIds, setSmsFilterUserIds] = useState<number[] | null>(null);
  const [pushFilterUserIds, setPushFilterUserIds] = useState<number[] | null>(null);

  const { data: emailUsersData } = useApiQuery<IEmailLogUser[]>({
    queryKey: [endpointsMetadata.communicationLogs.getEmailLogUsers],
    endpoint: endpointsMetadata.communicationLogs.getEmailLogUsers,
    globalIsLoading: false,
  });

  const { data: smsUsersData } = useApiQuery<ISmsLogUser[]>({
    queryKey: [endpointsMetadata.communicationLogs.getSmsLogUsers],
    endpoint: endpointsMetadata.communicationLogs.getSmsLogUsers,
    globalIsLoading: false,
  });

  const { data: pushUsersData } = useApiQuery<IPushLogUser[]>({
    queryKey: [endpointsMetadata.communicationLogs.getPushLogUsers],
    endpoint: endpointsMetadata.communicationLogs.getPushLogUsers,
    enabled: canSeePushLogs,
    globalIsLoading: false,
  });

  const emailUserOptions = useMemo(
    () => (emailUsersData ?? []).map((u) => ({ id: u.id, name: `${u.name} (${u.email ?? ""})` })),
    [emailUsersData]
  );

  const smsUserOptions = useMemo(
    () => (smsUsersData ?? []).map((u) => ({ id: u.id, name: `${u.name} (${u.phone ?? ""})` })),
    [smsUsersData]
  );

  // Only a name: devices are listed on each notification, not in the filter.
  const pushUserOptions = useMemo(
    () => (pushUsersData ?? []).map((u) => ({ id: u.id, name: u.name })),
    [pushUsersData]
  );

  const totalEmailActiveFilters =
    (emailFilterUserIds?.length ?? 0) + (emailFilterEmailTypes?.length ?? 0);
  const totalSmsActiveFilters = smsFilterUserIds?.length ?? 0;
  const totalPushActiveFilters = pushFilterUserIds?.length ?? 0;

  const clearEmailFilters = () => {
    setEmailFilterUserIds(null);
    setEmailFilterEmailTypes(null);
  };

  const clearSmsFilters = () => {
    setSmsFilterUserIds(null);
  };

  const clearPushFilters = () => {
    setPushFilterUserIds(null);
  };

  const onTabChange = (index: number) => {
    setActiveIndex(index);
    clearEmailFilters();
    clearSmsFilters();
    clearPushFilters();
  };

  const activeFilterCount =
    activeIndex === EMAIL_TAB_INDEX
      ? totalEmailActiveFilters
      : activeIndex === SMS_TAB_INDEX
        ? totalSmsActiveFilters
        : totalPushActiveFilters;

  const openActiveSidebar = () => {
    if (activeIndex === EMAIL_TAB_INDEX) setIsEmailSidebarOpen(true);
    else if (activeIndex === SMS_TAB_INDEX) setIsSmsSidebarOpen(true);
    else if (activeIndex === PUSH_TAB_INDEX) setIsPushSidebarOpen(true);
  };

  return {
    canSeePushLogs,
    activeIndex,
    onTabChange,
    activeFilterCount,
    openActiveSidebar,
    isEmailSidebarOpen,
    setIsEmailSidebarOpen,
    isSmsSidebarOpen,
    setIsSmsSidebarOpen,
    isPushSidebarOpen,
    setIsPushSidebarOpen,
    emailFilterUserIds,
    setEmailFilterUserIds,
    emailFilterEmailTypes,
    setEmailFilterEmailTypes,
    emailTypeOptions,
    smsFilterUserIds,
    setSmsFilterUserIds,
    pushFilterUserIds,
    setPushFilterUserIds,
    emailUserOptions,
    smsUserOptions,
    pushUserOptions,
    totalEmailActiveFilters,
    totalSmsActiveFilters,
    clearEmailFilters,
    clearSmsFilters,
    clearPushFilters,
  };
};
